import { ReactNode, useMemo } from 'react'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import Checkbox from '@ui-components-3/ui/lib/components/Checkbox'
import { formatTicketDate } from './util'
import type { CustomField, CrisisNoteDetails, Ticket } from './types'
import {
  SPECIAL_FIELD_IDS,
  CATEGORY_FIELD_ID,
  ORDERED_FIELD_IDS,
  HEADER_FIELDS,
  HEADER_FIELD_IDS,
} from './constants/crisisNotes'

type LabelValueProps = {
  label: string
  children?: ReactNode
  className?: string
}

const LabelValue = ({ label, children, className }: LabelValueProps) => {
  const displayValue = children === null || children === undefined || children === '' ? '-' : children

  return (
    <div className={classNames('flex h-full flex-col items-start gap-1 min-w-0', className)}>
      <h6 className="typography-body font-semibold text-neutral-500">{label}</h6>
      <p className="typography-body-l mt-auto font-medium text-neutral-800 w-full break-words overflow-wrap-anywhere">
        {displayValue}
      </p>
    </div>
  )
}

type CrisisNoteDetailsViewProps = {
  details: CrisisNoteDetails
  assignee: string
  ticketData?: Ticket
}

const CrisisNoteDetailsView = ({ details, assignee, ticketData }: CrisisNoteDetailsViewProps) => {
  const customFieldsMap = useMemo(() => {
    const map = new Map<string, CustomField>()
    if (details.customFields) {
      details.customFields.forEach((field) => {
        map.set(field.fieldId, field)
      })
    }
    return map
  }, [details.customFields])

  const renderFieldValue = (fieldId: string) => {
    const field = customFieldsMap.get(fieldId)
    if (!field) return null

    switch (fieldId) {
      case SPECIAL_FIELD_IDS.CSSRS_COMPLETED:
        return (
          <label className="flex flex-row items-center gap-1">
            <Checkbox disabled checked={field.value === 'true'} className="mx-1" />
            {field.value === 'true' ? 'Complete' : 'Incomplete'}
          </label>
        )
      case SPECIAL_FIELD_IDS.CRISIS_INTERVENTIONS:
        return <span className="block whitespace-pre-line">{field.value?.split(',').join('\n')}</span>
      case SPECIAL_FIELD_IDS.MEMBER_FIRST_CALLED:
      case SPECIAL_FIELD_IDS.MEMBER_SECOND_CALLED:
      case SPECIAL_FIELD_IDS.MEMBER_THIRD_CALLED:
        return field.value && <span className="whitespace-pre">{formatTicketDate(field.value, true)}</span>
      case SPECIAL_FIELD_IDS.EMERGENCY_SERVICES_CONTACT_TIME:
      case SPECIAL_FIELD_IDS.EMERGENCY_SERVICES_ARRIVAL_TIME:
        return field.value && <span className="whitespace-pre">{field.value}</span>
      default:
        return field.value
    }
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {HEADER_FIELDS.map((fieldId) => {
          const field = customFieldsMap.get(fieldId)
          if (!field) return null

          // Only use memberId fallback for MEMBER_ID field
          const displayValue =
            fieldId === HEADER_FIELD_IDS.MEMBER_ID ? field?.value || ticketData?.memberId || '-' : field?.value || '-'

          return (
            <LabelValue key={fieldId} label={field.fieldName}>
              {displayValue}
            </LabelValue>
          )
        })}
      </div>
      <hr />
      <div className="flex flex-row flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <h2 className="typography-h4">Crisis Response Note</h2>
        <p className="typography-body">
          <b className="mr-2 font-medium">Assignee:</b>
          {assignee}
        </p>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {ORDERED_FIELD_IDS.map((fieldId) => {
          const field = customFieldsMap.get(fieldId)
          if (!field) return null
          return (
            <LabelValue
              key={fieldId}
              label={field.fieldName}
              className={fieldId === CATEGORY_FIELD_ID ? 'md:col-span-2' : undefined}
            >
              {renderFieldValue(fieldId)}
            </LabelValue>
          )
        })}
      </div>
    </>
  )
}

export default CrisisNoteDetailsView
